import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { secureSignIn } from "@/lib/secureLogin";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: string | null;
  userRoleId: string | null;
  roleId: string | null;
  ministryScope: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeRole = (rawRole: string | null): string | null => {
  if (!rawRole) return null;
  const normalized = rawRole.trim().toLowerCase().replace(/\s+/g, "_");

  if (normalized === "member") return "volunteer";
  if (normalized === "treasurer") return "staff";
  if (normalized === "church_admin") return "admin";

  return normalized;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [userRoleId, setUserRoleId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [ministryScope, setMinistryScope] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchRole = async (userId: string) => {
    const scoreRoleRow = (row: any) => {
      const normalized = normalizeRole(row?.role ?? null);
      const hasChurch = row?.church_id ? 1 : 0;
      const hasRoleId = row?.role_id ? 1 : 0;
      const roleRank = normalized === "admin" ? 3 : normalized === "staff" ? 2 : normalized === "volunteer" ? 1 : 0;
      const updatedAt = Number.isFinite(Date.parse(String(row?.updated_at || ""))) ? Date.parse(String(row?.updated_at || "")) : 0;
      return [hasChurch, hasRoleId, roleRank, updatedAt] as const;
    };

    const pickBestRoleRow = (rows: any[]) => {
      if (!rows || rows.length === 0) return null;
      return rows.reduce((best, current) => {
        if (!best) return current;
        const bestScore = scoreRoleRow(best);
        const currentScore = scoreRoleRow(current);
        for (let i = 0; i < bestScore.length; i++) {
          if (currentScore[i] > bestScore[i]) return current;
          if (currentScore[i] < bestScore[i]) return best;
        }
        return best;
      }, null as any);
    };

    const fetchRoleRows = async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", userId);

      if (error) throw error;
      return data || [];
    };

    let rows = await fetchRoleRows();
    let data = pickBestRoleRow(rows);

    // Fallback: if no row exists (trigger may not have fired), create it via RPC
    if (!data) {
      await supabase.rpc("ensure_user_role" as any, { _user_id: userId });
      rows = await fetchRoleRows();
      data = pickBestRoleRow(rows);
      if (!data) {
        // Fail closed: never assume admin when role lookup fails.
        setRole(null);
        setUserRoleId(null);
        setRoleId(null);
        setMinistryScope(null);
        return;
      }
    }

    setRole(normalizeRole(data?.role ?? null));
    setUserRoleId(data?.id ?? null);
    setRoleId((data as any)?.role_id ?? null);
    setMinistryScope((data as any)?.ministry_scope ?? null);
  };

  useEffect(() => {
    let initialised = false;

    // getSession handles the initial page-load: await the role before clearing loading
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        try {
          await fetchRole(session.user.id);
        } catch {
          // role fetch failed — continue with null role
        }
      }
      initialised = true;
      setLoading(false);
    });

    // onAuthStateChange handles subsequent sign-in / sign-out after the initial load
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchRole(session.user.id).finally(() => {
            if (initialised) setLoading(false);
          });
        } else {
          setRole(null);
          setUserRoleId(null);
          setRoleId(null);
          setMinistryScope(null);
          if (initialised) setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await secureSignIn(email, password);
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/setup` },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.replace("/");
  };

  const refreshRole = async () => {
    if (user) await fetchRole(user.id);
  };

  return (
    <AuthContext.Provider value={{ session, user, role, userRoleId, roleId, ministryScope, loading, signIn, signUp, signOut, refreshRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
